import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {ActionPerformed} from "@capacitor/push-notifications";

interface DeepLinkClick {
  deepLinkType: string;
  eventType: "url"|"pushNotify";
  url?: string;
  pushNotifyData?: any;
  rawNotificationEvent?: ActionPerformed;
  userUuid?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DeepLinkService {

  private deepLinkClicksToHandleAfterLogin: DeepLinkClick[] = [];

  constructor(
    private router: Router
  ) { }

  public queueDeepLinkClickToHandleAfterLogin (notification: ActionPerformed) {
    if (!notification.notification || !notification.notification.data) {
      return;
    }

    const data = notification.notification.data;

    if (!data.eventType || !data.userUuid) {
      console.log('Malformed push notification click event data!', data);
      return;
    }

    this.deepLinkClicksToHandleAfterLogin.push({
      deepLinkType: data.eventType,
      eventType: 'pushNotify',
      userUuid: data.userUuid,
      pushNotifyData: data,
      rawNotificationEvent: notification,
    });
  }

  public queueRequestedUrlToHandleAfterLogin (url: string) {
    if (!url || url.length < 2) {
      return;
    }

    let parsedUrl;
    // Check if the URL has a protocol and domain
    if (url.startsWith('http://') || url.startsWith('https://')) {
      parsedUrl = new URL(url);
    } else {
      // Assume the URL is a relative path and use the current origin
      parsedUrl = new URL(url, window.location.origin);
    }
  
    // Extract the basename (file name) from the pathname
    const pathParts = parsedUrl.pathname.split('/');
    const basename = pathParts.pop() || pathParts.pop(); // Handle cases where pathname ends with a slash

    // Blacklist any URLs that direct to pages in ./pages/public/ and ./pages/private/landing folders
    // (cf ./front-end-gmo/mobile-app/src/app/app-routing.module.ts for definition of URLs)
    const blacklistedURLs = [
      'login-normal',
      'logging-out',
      'register',
      'application-reference',
      'confirm-email',
      'recover',
      'welcome',
      'walkthrough',
      'bad-api-version',
      'account-block-page',
      'collect-testimony',
      'landing'
    ];

    if (blacklistedURLs.indexOf(basename.toLowerCase()) == -1) {
      console.log('URL with basename = ' + basename.toLowerCase() + " was pushed");
      this.deepLinkClicksToHandleAfterLogin.push({
        deepLinkType: 'url',
        eventType: 'url',
        url,
      });
    }
  }

  async dispatchQueuedDeepLinkClicks() {
    const currentUser = this.getCurrentlyLoggedInUser();
    let wasDispatched: boolean = false;
    for (let deepLinkClick of this.deepLinkClicksToHandleAfterLogin) {
      if (deepLinkClick.userUuid && deepLinkClick.userUuid !== currentUser.uuid) {
        continue;
      }

      if (!deepLinkClick.userUuid && deepLinkClick.eventType === 'pushNotify') {
        console.log('Deep link click without a bound userUuid!', deepLinkClick);
      }

      if (deepLinkClick.eventType === 'pushNotify') {
        wasDispatched = await this.handleDeepLinkFromPushNotification(deepLinkClick.rawNotificationEvent);
        if (wasDispatched) {
          break;
        }
      } else if (deepLinkClick.eventType === 'url') {
        if (deepLinkClick.url === '') {
          continue;
        }

        wasDispatched = await this.router.navigateByUrl(deepLinkClick.url);
        if (wasDispatched) {
          break;
        }
      }
    }

    this.deepLinkClicksToHandleAfterLogin = [];

    return wasDispatched;
  }

  async clearQueuedDeepLinkClicks() {
    this.deepLinkClicksToHandleAfterLogin = [];
  }

  async handleDeepLinkFromPushNotification(notification: ActionPerformed) {
    if (!notification.notification || !notification.notification.data) {
      return false;
    }

    const data = notification.notification.data;

    if (data.eventType === 'new_message_from_contact') {
      if (data.conversationUuid && data.channel) {
        await this.router.navigate(['/seeker-request', {
          conversation: data.conversationUuid,
          channel: data.channel,
        }]);
        return true;
      } else {
        console.log('Insufficient data in push notification for deep link to conversation with contact!');
        return false;
      }
    }

    // More deep link actions go here if needed

    return false;
  }

  private getCurrentlyLoggedInUser() {
    const ptyUserData = localStorage.getItem('pty_user_data');
    if (!ptyUserData || ptyUserData === '') {
      return null;
    }

    let userStorage: any = {};
    if (ptyUserData) {
      userStorage = JSON.parse(ptyUserData);
    }

    if (!userStorage.user || !userStorage.user.uuid) {
      return null;
    }

    return userStorage.user;
  }
}


